import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
    setSelectedMaps,
    updateMapData,
    setMapData,
    updateSeatStatus,
    updateTimer,
    incrementCounter,
    setLoading,
    setError,
    resetData,
    filterMapData
} from '../../store/features/agent-map';
import { useLazyGetAgentMapDetailQuery } from 'services';
import { AgentMapDto, ResponseDto } from 'types';
import { socketService } from 'utils/agent-map/socket-service-singleton';

// Interface cho socket payloads
interface AgentStatusSocketPayload {
    extensionId: string;
    nextState: string;
    agentMapSeatId?: number;
    agentChangeStatusDate?: string;
    parsedTimestamp?: number;
}

interface CounterSocketPayload {
    agentMapSeatId: number;
    counterType: 'call' | 'chat';
}

export const useAgentMapStore = () => {
    socketService.initialize(); // Initialize socket when maps are selected

    const dispatch = useDispatch();
    const { selectedMaps, mapData, counters, timers, loading, error } = useSelector((state: RootState) => state.agentMap);
    const [fetchAgentMapDetail] = useLazyGetAgentMapDetailQuery();

    // Thêm biến lưu trữ map IDs đã được fetch
    const loadedMapIdsRef = useRef<Set<number>>(new Set());

    // Helper để tìm seat bằng extensionId - cần cho socket update
    const findSeatByExtensionId = useCallback((extensionId: string) => {
        for (const map of mapData) {
            const seat = map.seats.find(s => s.extensionId === extensionId);
            if (seat) return seat;
        }
        return null;
    }, [mapData]);

    // Không cần handleAgentStatusUpdate và handleCounterUpdate vì đã được xử lý bởi singleton service

    // Cập nhật timer cho một seat
    const updateSeatTimer = useCallback((agentMapSeatId: number, timestamp?: number) => {

        dispatch(updateTimer({
            seatId: agentMapSeatId,
            timestamp: timestamp || Date.now()
        }));
    }, [dispatch]);

    // Add this function above the selectMaps function
    const getAgentMapDetail = useCallback(async (mapId: number): Promise<AgentMapDto | null> => {
        try {
            dispatch(setLoading(true));
            const response = await fetchAgentMapDetail(mapId).unwrap();
            if (response && response.data) {
                loadedMapIdsRef.current.add(mapId);
                return response.data;
            }
            return null;
        } catch (error) {
            console.error(`Error fetching map ${mapId}:`, error);
            dispatch(setError(`Failed to load map ${mapId}`));
            return null;
        } finally {
            dispatch(setLoading(false));
        }
    }, [dispatch, fetchAgentMapDetail]);

    // Cải thiện hàm selectMaps để xử lý việc bỏ chọn map
    const selectMaps = useCallback((mapIds: number[], forceRefresh = false, preserveOnline = false) => {


        // Update the selected maps in Redux
        dispatch(setSelectedMaps(mapIds));

        if (forceRefresh) {
            // If force refresh, clear maps first to ensure clean data
            dispatch({
                type: 'agentMap/filterMapData',
                payload: {
                    mapIds: [],
                    preserveOnline
                }
            });

            // Load data for selected maps
            mapIds.forEach(mapId => {
                getAgentMapDetail(mapId).then(data => {
                    if (data) {
                        // Ensure we preserve capabilities for seats
                        const enhancedSeats = data.seats?.map(seat => ({
                            ...seat,
                            canCall: seat.canCall !== undefined ? seat.canCall : true,
                            canChat: seat.canChat !== undefined ? seat.canChat : true,
                        })) || [];

                        // Update map in Redux, preserving name
                        dispatch(updateMapData({
                            ...data,
                            seats: enhancedSeats
                        }));
                    }
                });
            });
        } else {
            // Just filter maps
            dispatch({
                type: 'agentMap/filterMapData',
                payload: {
                    mapIds,
                    preserveOnline
                }
            });
        }

        // Force UI update
        setTimeout(() => {
            dispatch({
                type: 'agentMap/updateTimestamp',
                payload: Date.now()
            });
        }, 10);
    }, [dispatch, getAgentMapDetail]);

    // Helper methods
    const getAllSeats = useCallback(() => {
        return mapData.flatMap(map => map.seats);
    }, [mapData]);

    // Lấy counter cho một seat
    const getSeatCounters = useCallback((seatId: number) => {
        const seatIdKey = seatId.toString();
        return counters[seatIdKey] || { call: 0, chat: 0 };
    }, [counters]);

    // Lấy timer cho một seat
    const getSeatTimer = useCallback((seatId: number) => {
        const seatIdKey = seatId.toString();
        return timers[seatIdKey] || 0;
    }, [timers]);

    // Tính thời gian đã trôi qua (seconds) cho một seat
    const getSeatSeconds = useCallback((seatId: number) => {
        const timer = getSeatTimer(seatId);
        if (!timer) return 0;
        return Math.floor((Date.now() - timer) / 1000);
    }, [getSeatTimer]);

    // Reset tất cả dữ liệu
    const reset = useCallback(() => {
        dispatch(resetData());
    }, [dispatch]);

    // Reset all agent map data
    const resetAgentMap = useCallback(() => {

        dispatch(resetData());
    }, [dispatch]);

    return {
        // State
        selectedMaps,
        mapData,
        counters,
        timers,
        loading,
        error,

        // Helper methods
        getAllSeats,
        getSeatCounters,
        getSeatTimer,
        getSeatSeconds,
        findSeatByExtensionId,

        // Actions
        selectMaps,
        updateSeatTimer,
        incrementCounter: (seatId: number, counterType: 'call' | 'chat') =>
            dispatch(incrementCounter({ seatId, counterType })),
        reset,
        resetAgentMap // Expose the reset function
    };
};
